// Typed harbours by function — the improved harbour classification (source
// /v1/ports). Each port is typed from AIS movement scores into one of three
// functional kinds:
//
//   feed/service      → fôr- / servicebaser  (mint token)
//   harvest/slaughter → slakte- / høstekaier (Pulse red token)
//   fishing           → fiskerihavner        (signal-dim blue-grey)
//
// This is the cleaner, authoritative companion to the heuristic Servicehavner
// (serviceports.ts) layer. Brand-aligned ISOTYPE pins (per pins.ts): a flat
// Deep glyph on a coloured circular token, sized by distinct-vessel traffic;
// on zoom-out they cluster into bubbles sized by harbour count. Opt-in.

import maplibregl from "maplibre-gl";

import { addPin, pinSVG } from "./pins";
import { anchorGlyph } from "./serviceports";

const API_BASE = import.meta.env.VITE_API_BASE;
const PORTS_LAYERS = ["ports-pin"];
const DEEP = "#082E22";
let popup: maplibregl.Popup | null = null;

type PortType = "feed/service" | "harvest/slaughter" | "fishing";
export const PORTS_META: Record<PortType, { label: string; color: string }> = {
  "feed/service": { label: "Fôr- / servicebase", color: "#7BE3C6" }, // bright mint
  "harvest/slaughter": { label: "Slakte- / høstekai", color: "#E63926" }, // Pulse red
  fishing: { label: "Fiskerihavn", color: "#8AA99A" }, // signal-dim
};

type PortProps = {
  c9: string;
  port_type: PortType;
  label: string;
  plant_name: string | null;
  n_vessels: number;
  share_aqua: number;
  dep_farm: number;
  dep_slaughter: number;
  feed_hub_score: number;
  harvest_collect_score: number;
};

function esc(s: string | null): string {
  return (s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function pct(x: number | null | undefined): string {
  return x == null ? "–" : `${Math.round(x * 100)} %`;
}

// --- ISOTYPE pin glyphs (Deep ink on a coloured marker) --------------------

function feedGlyph(ink: string): string {
  // Feed pellets falling into a hopper — three discs over an open trough.
  return (
    `<g fill="${ink}">` +
    `<circle cx="-5" cy="-9" r="2.2"/>` +
    `<circle cx="3" cy="-9" r="2.2"/>` +
    `<circle cx="-1" cy="-3" r="2.2"/>` +
    `</g>` +
    `<path d="M-10 1 L10 1 L4 11 L-4 11 Z" fill="none" stroke="${ink}" ` +
    `stroke-width="2.6" stroke-linejoin="round"/>`
  );
}

function harvestGlyph(ink: string): string {
  // Fish over a quay edge — a landed catch at the slaughter / collection kai.
  return (
    `<path d="M-11 -2 Q-2 -9 8 -5 Q11 -4 11 -3 Q11 -2 8 -1 Q-2 3 -11 -2 Z" fill="${ink}"/>` +
    `<path d="M-11 -2 L-15 -6 L-13 -2 L-15 2 Z" fill="${ink}"/>` +
    `<path d="M-12 7 L12 7" fill="none" stroke="${ink}" stroke-width="2.6" stroke-linecap="round"/>`
  );
}

export async function initPorts(map: maplibregl.Map): Promise<void> {
  let data: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  try {
    const res = await fetch(`${API_BASE}/v1/ports`, { cache: "no-store" });
    if (res.ok) data = (await res.json()) as GeoJSON.FeatureCollection;
  } catch {
    /* leave the layer empty if the feed is unavailable */
  }

  if (map.getSource("ports")) {
    (map.getSource("ports") as maplibregl.GeoJSONSource).setData(data);
    return;
  }

  await Promise.all([
    addPin(map, "pin-port-feed", pinSVG(PORTS_META["feed/service"].color, DEEP, feedGlyph(DEEP))),
    addPin(
      map,
      "pin-port-harvest",
      pinSVG(PORTS_META["harvest/slaughter"].color, DEEP, harvestGlyph(DEEP)),
    ),
    addPin(map, "pin-port-fishing", pinSVG(PORTS_META.fishing.color, DEEP, anchorGlyph(DEEP))),
  ]);

  // Cluster on zoom-out → overview of where the harbours bunch up; bubbles are
  // sized by harbour count and expand on click.
  map.addSource("ports", {
    type: "geojson",
    data,
    cluster: true,
    clusterRadius: 48,
    clusterMaxZoom: 9,
  });

  map.addLayer({
    id: "ports-pin",
    type: "symbol",
    source: "ports",
    filter: ["!", ["has", "point_count"]], // individual harbours; clusters are HTML bubbles
    layout: {
      visibility: "none", // opt-in
      "icon-image": [
        "match",
        ["get", "port_type"],
        "feed/service", "pin-port-feed",
        "harvest/slaughter", "pin-port-harvest",
        "pin-port-fishing",
      ],
      // Size carries traffic (distinct vessels seen), like the other port layers.
      "icon-size": [
        "interpolate", ["linear"], ["coalesce", ["get", "n_vessels"], 0],
        0, 0.8, 20, 1.0, 80, 1.3, 200, 1.55,
      ],
      "icon-anchor": "center",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  map.on("click", "ports-pin", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties as PortProps;
    const coords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
    const meta = PORTS_META[p.port_type] ?? PORTS_META.fishing;
    popup?.remove();
    popup = new maplibregl.Popup({ className: "onshore-tip", offset: 24, maxWidth: "260px" })
      .setLngLat(coords)
      .setHTML(
        `<div class="popup-label">${esc(meta.label)}</div>` +
          `<div class="onshore-name">${esc(p.label) || "Ukjent havn"}</div>` +
          (p.plant_name ? `<div class="onshore-meta">${esc(p.plant_name)}</div>` : "") +
          `<div class="onshore-meta">${p.n_vessels} fartøy</div>` +
          `<div class="onshore-meta">Avganger: anlegg ${pct(p.dep_farm)} · slakteri ${pct(p.dep_slaughter)}</div>` +
          `<div class="onshore-meta" style="opacity:.6;margin-top:6px">Driftstype avledet fra AIS-bevegelser</div>`,
      )
      .addTo(map);
  });
  map.on("mouseenter", "ports-pin", () => (map.getCanvas().style.cursor = "pointer"));
  map.on("mouseleave", "ports-pin", () => (map.getCanvas().style.cursor = ""));
}

export function setPortsVisible(map: maplibregl.Map, on: boolean): void {
  for (const id of PORTS_LAYERS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
  }
}
